// ✅ 完整覆蓋：ui/components/approval/ApprovalFlowPanel.tsx
// 用途：共用的黑灰底白字流程面板（可純檢視 / 可簽核）
// 重點：onDecision 改成可選；mode="view" 或 onDecision 未提供時，不顯示簽核操作區
//      showStages=false 時，不顯示簽核進度清單（避免重複顯示）

import React, { useMemo, useState } from "react";
import type { ApprovalFlow, ApprovalStageKey, ApprovalStageStatus } from "../../types/approval";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../Toast";
import { getDeptCodeByEmail, isGm, isLegal, isOps, isSuperUser } from "../../config/roles";

export type ApprovalDecisionInput = "APPROVED" | "REJECTED" | "RETURNED" | "DONE";

type StageLike = any;

function normStage(s: StageLike) {
  const stageKey: ApprovalStageKey = (s?.stageKey ?? s?.key) as ApprovalStageKey;
  const stageLabel: string = s?.stageLabel ?? s?.label ?? String(stageKey || "");

  const rawStatus = (s?.status ?? "NOT_STARTED") as ApprovalStageStatus;
  const status: ApprovalStageStatus =
    rawStatus === "PENDING" && stageKey !== "CO_REVIEW" ? "NOT_STARTED" : rawStatus;

  return {
    raw: s,
    stageKey,
    stageLabel,
    status,
    comment: s?.comment ?? "",
    decidedAt: s?.decidedAt,
    approverRole: s?.approverRole ?? s?.assigneeRole ?? s?.role,
    approverUserId: s?.approverUserId ?? s?.assigneeUid,
    approverName: s?.approverName ?? s?.assigneeName,
    deptDecisions: s?.deptDecisions,
  };
}

function isStageDone(status: string) {
  return status === "APPROVED" || status === "REJECTED" || status === "RETURNED" || status === "DONE";
}

function statusTone(status: string) {
  if (status === "APPROVED" || status === "DONE") return { bg: "rgba(34,197,94,0.12)", bd: "rgba(34,197,94,0.35)", fg: "#a7f3d0" };
  if (status === "REJECTED") return { bg: "rgba(239,68,68,0.12)", bd: "rgba(239,68,68,0.35)", fg: "#fecaca" };
  if (status === "RETURNED") return { bg: "rgba(251,191,36,0.12)", bd: "rgba(251,191,36,0.35)", fg: "#fde68a" };
  if (status === "PENDING") return { bg: "rgba(147,197,253,0.10)", bd: "rgba(147,197,253,0.25)", fg: "#bfdbfe" };
  return { bg: "rgba(255,255,255,0.05)", bd: "rgba(255,255,255,0.10)", fg: "rgba(255,255,255,0.70)" };
}

function fmtTime(v: any) {
  try {
    if (!v) return "";
    if (typeof v?.toDate === "function") return v.toDate().toLocaleString();
    const d = typeof v === "string" || typeof v === "number" ? new Date(v) : v;
    if (d instanceof Date && !isNaN(d.getTime())) return d.toLocaleString();
    return String(v);
  } catch {
    return "";
  }
}

export type ApprovalFlowPanelProps = {
  flow: ApprovalFlow | any | null;
  mode?: "view" | "action";
  /**
   * Whether to show the stage progress list.
   * Use false when progress is already displayed elsewhere (e.g., dedicated "簽核流程" modal).
   */
  showStages?: boolean;
  onDecision?: (
    stageKey: ApprovalStageKey,
    decision: ApprovalDecisionInput,
    comment?: string,
    deptCode?: string,
    isDirectSign?: boolean
  ) => Promise<void>;
};

export function ApprovalFlowPanel(props: ApprovalFlowPanelProps) {
  const { flow, onDecision, mode = "action", showStages = true } = props;
  const { user } = useAuth();
  const toast = useToast();

  const [comment, setComment] = useState<string>("");
  const [selectedDecision, setSelectedDecision] = useState<ApprovalDecisionInput | "APPROVE_AND_SEAL">("APPROVED");
  const [busy, setBusy] = useState(false);

  const stages = useMemo(() => (flow?.stages || []).map(normStage), [flow]);

  const currentStage = useMemo(() => {
    const curKey = flow?.currentStageKey;
    if (!curKey) return null;
    return stages.find((s) => s.stageKey === curKey) || null;
  }, [flow, stages]);

  const canAct = useMemo(() => {
    if (!user || !currentStage) return false;

    const email = (user.email || "").toLowerCase().trim();
    if (email && isSuperUser(email)) return true;

    const assignee = currentStage.approverUserId;
    if (assignee) return assignee === user.id;

    const role = String(currentStage.approverRole || "").toLowerCase();
    if (role === "legal") return isLegal(email);
    if (role === "ops") return isOps(email);
    if (role === "gm") return isGm(email);

    if (currentStage.stageKey === "CO_REVIEW") {
      const deptCode = getDeptCodeByEmail(email);
      if (!deptCode) return false;

      const raw = currentStage.deptDecisions;
      const current =
        Array.isArray(raw)
          ? raw.find((d: any) => d?.deptCode === deptCode)
          : raw && typeof raw === "object"
            ? raw[deptCode]
            : null;

      return !current || current.status === "PENDING" || current.status === "NOT_STARTED";
    }

    return false;
  }, [user, currentStage]);

  async function handleSubmitDecision() {
    if (!currentStage) return;

    if (mode !== "action") {
      toast.showToast("目前為純檢視模式。", "warning");
      return;
    }
    if (!onDecision) {
      toast.showToast("此頁未接 onDecision（無法送出簽核）。", "error");
      return;
    }
    if (!canAct) {
      toast.showToast("你沒有權限對此階段做決策。", "error");
      return;
    }
    if ((selectedDecision === "REJECTED" || selectedDecision === "RETURNED") && !comment.trim()) {
      toast.showToast("駁回/退回時請填寫原因。", "error");
      return;
    }

    const deptCode = (() => {
      if (currentStage.stageKey !== "CO_REVIEW") return undefined;
      const email = (user?.email || "").toLowerCase().trim();
      return getDeptCodeByEmail(email) || undefined;
    })();

    try {
      setBusy(true);
      const normalizedDecision: ApprovalDecisionInput = (selectedDecision === "APPROVE_AND_SEAL" ? "APPROVED" : selectedDecision) as ApprovalDecisionInput;
      await onDecision(currentStage.stageKey, normalizedDecision, comment, deptCode);
      setComment("");
      setSelectedDecision("APPROVED");
      toast.showToast("已送出簽核決策。", "success");
    } catch (e: any) {
      toast.showToast(e?.message || "送出失敗", "error");
    } finally {
      setBusy(false);
    }
  }

  if (!flow) {
    return (
      <div
        style={{
          padding: 14,
          borderRadius: 12,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "rgba(255,255,255,0.75)",
        }}
      >
        尚未建立簽核流程（可能尚未送出呈核）。
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* stages (optional) */}
      {!showStages ? null : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {stages.map((s) => {
            const isCurrent = s.stageKey === flow?.currentStageKey;
            const done = isStageDone(String(s.status));
            const tone = statusTone(String(s.status));

            return (
              <div
                key={s.stageKey}
                style={{
                  padding: 12,
                  borderRadius: 14,
                  border: isCurrent ? "1px solid rgba(255,255,255,0.28)" : "1px solid rgba(255,255,255,0.12)",
                  background: isCurrent ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.035)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <div style={{ fontWeight: 900, color: "#fff" }}>
                    {s.stageLabel}{" "}
                    <span style={{ fontWeight: 600, color: "rgba(255,255,255,0.60)" }}>
                      （{s.approverName || "未指派"}）
                    </span>
                  </div>

                  <span
                    style={{
                      fontSize: 12,
                      padding: "3px 10px",
                      borderRadius: 999,
                      background: tone.bg,
                      border: `1px solid ${tone.bd}`,
                      color: tone.fg,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {String(s.status)}
                  </span>
                </div>

                {/* CO_REVIEW dept decisions */}
                {s.stageKey === "CO_REVIEW" &&
                  (() => {
                    const raw = (s as any).deptDecisions;
                    const list: Array<{ deptCode: string; status?: string; comment?: string; actorName?: string }> =
                      Array.isArray(raw)
                        ? raw
                        : raw && typeof raw === "object"
                          ? Object.keys(raw).map((k) => ({ deptCode: k, ...(raw[k] || {}) }))
                          : [];

                    if (list.length === 0) return null;

                    return (
                      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        {list.map((d, idx) => {
                          const t = statusTone(String(d.status || "PENDING"));
                          return (
                            <div
                              key={idx}
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "center",
                                flexWrap: "wrap",
                                padding: "8px 10px",
                                borderRadius: 12,
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontSize: 12,
                                color: "rgba(255,255,255,0.78)",
                              }}
                            >
                              <span style={{ fontWeight: 900, color: "#fff" }}>{d.deptCode || "?"}</span>
                              <span style={{ padding: "2px 8px", borderRadius: 999, background: t.bg, border: `1px solid ${t.bd}`, color: t.fg }}>
                                {d.status || "PENDING"}
                              </span>
                              {d.actorName && <span style={{ color: "rgba(255,255,255,0.55)" }}>by {d.actorName}</span>}
                              {d.comment && <span style={{ color: "rgba(255,255,255,0.75)" }}>— {d.comment}</span>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                {!!s.comment && (
                  <div style={{ marginTop: 10, fontSize: 13, color: "rgba(255,255,255,0.82)" }}>
                    <span style={{ fontWeight: 900, color: "#fff" }}>備註：</span>
                    {s.comment}
                  </div>
                )}

                {done && s.decidedAt && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                    決策時間：{fmtTime(s.decidedAt)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* action area */}
      {mode === "view" || !onDecision ? null : (
        <div
          style={{
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <div style={{ fontWeight: 950, marginBottom: 10, color: "#fff" }}>簽核操作</div>

          {!currentStage ? (
            <div style={{ color: "rgba(255,255,255,0.60)" }}>此簽核流程沒有可操作的階段。</div>
          ) : !canAct ? (
            <div style={{ color: "rgba(255,255,255,0.60)" }}>
              目前階段：{currentStage.stageLabel}（你沒有權限操作）
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <label style={{ fontWeight: 800, color: "rgba(255,255,255,0.85)" }}>決策</label>

                <select
                  className="input"
                  value={selectedDecision}
                  onChange={(e) => setSelectedDecision(e.target.value as any)}
                  style={{ maxWidth: 240 }}
                >
                  <option value="APPROVED">核准</option>
                  {flow?.module === "CONTRACT_REVIEW" ? (
                    <option value="APPROVE_AND_SEAL">核准並准予用印</option>
                  ) : null}
                  <option value="RETURNED">退回</option>
                  <option value="REJECTED">駁回</option>
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                <label style={{ fontWeight: 800, color: "rgba(255,255,255,0.85)" }}>備註</label>

                <textarea
                  className="input"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="（退回/駁回請務必填寫原因）"
                  style={{
                    background: "#151515",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                  disabled={busy}
                />
              </div>

              <button
                className="btn"
                onClick={handleSubmitDecision}
                disabled={busy}
                style={{
                  width: "100%",
                  background: "#ffffff",
                  color: "#111",
                  fontWeight: 900,
                  borderRadius: 12,
                }}
              >
                {busy ? "送出中..." : "送出決策"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ApprovalFlowPanel;
 